import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, DownloadSimple, Receipt, FileDoc, Certificate } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABELS = {
  recu: { label: 'Recu', color: '#2D5A4C', icon: Receipt },
  devis: { label: 'Devis', color: '#00AEEF', icon: FileDoc },
  attestation: { label: 'Attestation', color: '#FF8E00', icon: Certificate },
  facture: { label: 'Facture', color: '#EA3C3A', icon: FileDoc },
  autre: { label: 'Autre', color: '#5E6D66', icon: FileText },
};

export default function ClientDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = filter !== 'all' ? `?type_document=${filter}` : '';
        const { data } = await axios.get(`${API}/api/client/documents${params}`, { withCredentials: true });
        setDocuments(data.items || []);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [filter]);

  const handleDownload = async (doc) => {
    try {
      const { data } = await axios.get(`${API}/api/client/documents/${doc.id}/download`, { withCredentials: true });
      if (data.download_url) {
        window.open(data.download_url, '_blank');
        toast.success('Telechargement lance');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur telechargement');
    }
  };

  const filters = [
    { key: 'all', label: 'Tous' },
    { key: 'recu', label: 'Recus' },
    { key: 'devis', label: 'Devis' },
    { key: 'attestation', label: 'Attestations' },
  ];

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00AEEF]" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-documents-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes documents</h1>
        <p className="text-sm text-[#5E6D66] mt-1">Recus, devis et attestations disponibles au telechargement</p>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2" data-testid="client-doc-filters">
        {filters.map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setLoading(true); setFilter(f.key); }}
            className={filter === f.key ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : 'border-[#E8E6E1]'}
            data-testid={`client-doc-filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <Card className="border-[#E8E6E1]" data-testid="client-documents-list">
        <CardContent className="p-0">
          {documents.length === 0 ? (
            <div className="py-16 text-center">
              <FileText size={36} className="mx-auto text-[#E8E6E1] mb-3" />
              <p className="text-[#5E6D66]">Aucun document disponible</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E8E6E1]">
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Document</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Taille</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const typeInfo = DOC_TYPE_LABELS[doc.type_document] || DOC_TYPE_LABELS.autre;
                  const Icon = typeInfo.icon;
                  const size = doc.size ? (doc.size > 1024 * 1024 ? `${(doc.size / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(doc.size / 1024)} Ko`) : '-';
                  return (
                    <TableRow key={doc.id} className="border-[#E8E6E1] hover:bg-[#FAFAF8]">
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${typeInfo.color}12` }}>
                            <Icon size={16} style={{ color: typeInfo.color }} />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-[#1A2421]">{doc.titre || doc.original_filename}</p>
                            <p className="text-[10px] text-[#5E6D66]">{doc.original_filename}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]" style={{ borderColor: typeInfo.color, color: typeInfo.color }}>
                          {typeInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-[#5E6D66]">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-[#5E6D66]">{size}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#00AEEF]" onClick={() => handleDownload(doc)} data-testid={`client-download-${doc.id}`}>
                          <DownloadSimple size={12} className="mr-1" /> Telecharger
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
